import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { withBasePath } from '_config/basePath';
import { prepareAuthHeaders } from './auth';

export interface Document {
  title: string;
  id: string;
  content: string;
  updated_at: string;
  line_spacing: number;
  text_font: string;
  heading_font: string;
  nb_copy_paste: number;
  paragraph_numbering: boolean;
  author: string,
  thanks: string,
  abbreviations: object;
  index: object;
  level: number;
  created_at: Date;
  subtitle: string;
  ref_display: string;
  ref_style: string;
  is_prof: boolean;
  last_seen: string;
  is_accessible: boolean;
  nb_new_comments: number;
  nb_pages: number;
  nb_paste: number;
}

export interface DocumentTemplate {
  id: string;
  university: string;
  faculty: string;
  school_year: string;
  discipline: string;
  research_director: string;
  logo: string;
}

export interface DocumentsResponse {
  documents: Array<Document>;
}

export interface CreateDocumentRequest {
  title: string,
  subtitle: string,
  content: any | null
}

export interface CreateDocumentResponse {
  document_id: string
}

export interface DeleteDocumentRequest {
  id: string
}

export interface DocumentRight {
  email: string,
  document_id: string,
  level: number,
  is_prof: boolean
}

export interface DocumentRights {
  rights: Array<DocumentRight>
}

export interface StudentDocument {
  document_id: string,
  prof_id: string,
  firstname: string,
  lastname: string,
  last_seen: number,
  title: string,
  is_accessible: boolean,
  nb_new_msg: number,
  nb_new_comment: number
}




export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: withBasePath(''),
    prepareHeaders: prepareAuthHeaders,
    credentials: 'include',
  }),
  endpoints: (builder) => ({
    getAllDocuments: builder.mutation<DocumentsResponse, null>({
      query: () => ({
        url: `/api/documents`,
        method: 'GET',
      }),
    }),
    getDocument: builder.mutation<Document, null>({
      query: (id) => ({
        url: `/api/documents`,
        method: 'GET',
        params: { id },
      }),
      transformResponse: (response: DocumentsResponse) => response.documents[0]
    }),
    updateDocument: builder.mutation<null, any>({
      query: (document) => ({
        url: `/api/documents`,
        method: 'PATCH',
        body: document
      })
    }),
    getDocumentRights: builder.query<Array<DocumentRight>, any>({
      query: (document_id) => ({
        url: `/api/documents/rights`,
        method: 'GET',
        params: { document_id },
      }),
      transformResponse: (response: any) => response.document_rights
    }),

    updateDocumentRights: builder.mutation<null, any>({
      query: (documentRighsUpdate) => ({
        url: `/api/documents/rights`,
        method: 'PATCH',
        body: documentRighsUpdate
      })
    }),
    createDocument: builder.mutation<CreateDocumentResponse, CreateDocumentRequest>({
      query: (document) => ({
        url: `/api/documents`,
        method: 'PUT',
        body: document
      })
    }),
    deleteDocument: builder.mutation<null, any>({
      query: (document) => ({
        url: `/api/documents`,
        method: 'DELETE',
        body: document
      })
    }),
    getDocumentTemplate: builder.mutation<DocumentTemplate, null>({
      query: (document_id) => ({
        url: `/api/documents/template`,
        method: 'GET',
        params: { document_id },
      }),
      transformResponse: (response: any) => response.document_template
    }),
    updateDocumentTemplate: builder.mutation<null, any>({
      query: (documentTemplate) => ({
        url: `/api/documents/template`,
        method: 'PATCH',
        body: documentTemplate
      })
    }),
    getStudentsDocuments: builder.mutation<Array<StudentDocument>, null>({
      query: () => ({
        url: `/api/documents/students`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        return response.students_documents;
      }
    }),
    updateComments: builder.mutation<null, any>({
      query: (body) => ({
        url: `/api/documents/comments`,
        method: 'PATCH',
        body
      })
    }),
    deleteDocumentForProf: builder.mutation<null, any>({
      query: (body) => ({
        url: `/api/documents/rights`,
        method: 'DELETE',
        body 
      })
    }),
    getDocumentClass: builder.mutation<any, any>({
      query: (body) => ({
        url: `/api/documents/class`,
        method: 'GET',
        params: body
      }),
    }),
  }),
});

export const {
  useGetDocumentRightsQuery, useUpdateDocumentRightsMutation, useGetAllDocumentsMutation, useGetDocumentMutation,
  useGetDocumentTemplateMutation, useUpdateDocumentMutation, useCreateDocumentMutation, useDeleteDocumentMutation,
  useUpdateDocumentTemplateMutation, useGetStudentsDocumentsMutation, useUpdateCommentsMutation, useDeleteDocumentForProfMutation, useGetDocumentClassMutation } = documentsApi;

export const {
  endpoints: { getAllDocuments, getDocument, updateDocument, getDocumentTemplate },
} = documentsApi;
